import { useQuery } from '@apollo/react-hooks'
import type { Theme } from '@material-ui/core'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import { createStyles, withStyles } from '@material-ui/core'
import {
  ConfirmationDialog,
  HtmlContent,
  Spinner,
  Typography,
  useClientOptions,
  useDialogs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useUser,
  Grid
} from '@paintscout/ui'

import type { Activity as ActivityInterface, SentActivityDetails } from '@paintscout/api'
import { gql } from 'apollo-boost'
import { useSnackbar } from 'notistack'
import React, { useCallback, useState } from 'react'
import { getObjectLabels } from '@paintscout/util/builder'
import * as Sentry from '@sentry/react'
import Quote from '../../Quote'
import QuotePdfDialog from '../QuotePdfDialog'
import { useQuote } from '../../context'
export interface QuoteRevisionDialog extends Omit<DialogProps, 'message' | 'title'> {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  url: string
  activity?: ActivityInterface & { details: SentActivityDetails }
  onConfirm?: () => void
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    input: {
      minWidth: 300,
      [theme.breakpoints.up('md')]: {
        minWidth: 350
      }
    },
    email: {
      overflowY: 'auto'
    },
    emailTo: {},
    emailSubject: {
      fontWeight: theme.typography.fontWeightMedium
    },
    emailContent: {
      color: theme.palette.text.secondary,
      textAlign: 'left',
      whiteSpace: 'pre-line',
      wordBreak: 'break-all',
      marginTop: theme.spacing(0.5)
    },
    restoreButton: {
      marginRight: theme.spacing(2)
    }
  })

function QuoteRevisionDialog({ classes, url, onConfirm, activity, ...props }: QuoteRevisionDialog) {
  const { enqueueSnackbar } = useSnackbar()
  const { isCrew, isViewer } = useUser()
  const { quote: currentVersion } = useQuote()
  const { options, clientId } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const [viewRevision, setViewRevision] = useState<boolean>(true)
  const [revisionError, setRevisionError] = useState(false)
  const { email } = activity?.details ?? {}
  const to = email?.to ?? []
  const subject = email?.subject ?? ''
  const content = email?.content ?? ''

  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()

  const { data, loading } = useQuery(
    gql`
      query urlDoc($id: String!) {
        urlDoc(id: $id) {
          _id
          _rev
          quote
          view
          link
        }
      }
    `,
    {
      variables: {
        id: url
      },
      skip: !url,
      onError: useCallback((error) => {
        console.error(error)

        enqueueSnackbar(`Unable to load revision`, { variant: 'error' })
        if (!email.content && !email.subject) {
          if (onConfirm) {
            onConfirm()
          }
        } else {
          setViewRevision(false)
          setRevisionError(true)
        }
      }, [])
    }
  )

  const { quote, view, _rev } = data?.urlDoc || {}
  const { save } = useQuote()

  const handleRestore = () => {
    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message: `This will overwrite the current ${objectLabels.quote.value} and revert any changes you've made since this revision.`,
      scary: true,
      onCancel: dismissDialog,
      onConfirm: async () => {
        try {
          await save({
            ...quote,
            status: {
              ...quote.status,
              updated: Date.now()
            },
            dates: {
              ...quote.dates,
              updated: Date.now()
            },
            child_invoice: quote.child_invoice || currentVersion.child_invoice,
            depositInfo: quote.depositInfo || currentVersion.depositInfo,
            isRestored: true
          })
          enqueueSnackbar(`Successfully restored  ${objectLabels.quote.value} from revision!`, { variant: 'success' })
          dismissAllDialogs()
        } catch (error) {
          Sentry.captureException(error, { tags: { quoteId: quote._id } })
          enqueueSnackbar(`Unable to restore ${objectLabels.quote.value} from revision. `, { variant: 'error' })
          dismissDialog()
        }
      }
    })
  }

  function handleDownloadPDF() {
    openDialog(QuotePdfDialog, {
      quoteRev: _rev,
      quote,
      companyId: clientId,
      options,
      view: view === 'work-order' ? view : 'quote',
      silent: true,
      revision: true,
      revisionQuoteUrl: url,
      onConfirm: dismissDialog
    })
  }

  return (
    <Dialog fullWidth={true} maxWidth="lg" {...props}>
      <DialogTitle>View Revision</DialogTitle>
      <DialogContent>
        {viewRevision && (
          <div className={classes.root} style={{ pointerEvents: 'none' }}>
            <Grid container spacing={2} alignItems={'center'} direction={'column'}>
              <Grid item xs={12}>
                {loading ? <Spinner /> : <Quote quote={quote} view={view} />}
              </Grid>
            </Grid>
          </div>
        )}
        {!viewRevision && (
          <div className={classes.root}>
            <Grid container spacing={2} alignItems={'center'} direction={'column'}>
              <Grid item xs={12}>
                <div className={classes.email}>
                  <Typography variant="body1" component="h5" className={classes.emailSubject}>
                    {subject}
                  </Typography>
                  <Typography variant="subtitle2" component="h4" className={classes.emailTo}>
                    to {to.join(', ')}
                  </Typography>
                  <HtmlContent content={content} classes={{ root: classes.emailContent }} />
                </div>
              </Grid>
            </Grid>
          </div>
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          <div style={{ display: 'flex' }}>
            {activity && email && (
              <Button disabled={revisionError} variant="text" onClick={() => setViewRevision(!viewRevision)}>
                {viewRevision
                  ? 'View Email'
                  : revisionError
                    ? 'Revision Not Found'
                    : `View ${objectLabels.quote.value}`}
              </Button>
            )}
            {viewRevision && !isCrew && !isViewer && view !== 'work-order' && (
              <Button variant={'contained'} className={classes.restoreButton} onClick={handleRestore}>
                {`Restore ${objectLabels.quote.value}`}
              </Button>
            )}
          </div>
        }
      >
        {viewRevision && (
          <Button variant="text" onClick={handleDownloadPDF}>
            Download PDF
          </Button>
        )}
        <Button variant={'contained'} onClick={onConfirm}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withStyles(styles)(QuoteRevisionDialog)
